import { Flex, useMantineTheme, Title } from '@mantine/core';
import { FlexExtended } from '@dealstack/frontend/shared/components/Flex/FlexExtended';
import {
	GetKYCInvestorInstanceRes,
	KycWorkflowSettingResponseDto,
} from '@dealstack/shared/lib/generated';
import {
	useTranslationInvestor,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useDeclarations } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/Declarations/useDeclarations';
import { DetailRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/component/DetailRow';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { PhoneNumber } from '@dealstack/frontend/shared/components/PhoneNumber/PhoneNumber';
import { formatBirthday } from '@dealstack/shared/lib/format/dates/date';
import { DetailInfoText } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/component/DetailInfoText';
import { Step } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/types';
import {
	getInvestmentDisplayType,
	getInvestmentDisplayTypeBadgeColor,
	getInvestmentDisplayTypeLabel,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/components/utils';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge';
import { useParams } from 'react-router-dom';
import { StyledLink } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/Overview/styled';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils';

interface Props {
	currentStep: Step;
	currentInvestor?: GetKYCInvestorInstanceRes;
	kycSetting?: KycWorkflowSettingResponseDto;
}

export const Overview = ({ currentStep, currentInvestor, kycSetting }: Props) => {
	const theme = useMantineTheme();
	const tMipManage = useTranslationMipManage();
	const tInvestor = useTranslationInvestor();
	const { dealId, mipId } = useParams();

	const investmentType = currentInvestor?.investmentType;
	const investorName = currentInvestor
		? `${currentInvestor.user.firstName} ${currentInvestor.user.lastName}`.trim()
		: '';

	const investmentsPath = investmentType
		? `${mipManagePath(MIP_MANAGE_ROUTES.INVESTMENTS, {
				dealId: dealId ?? '',
				mipId: mipId ?? '',
			})}?globalSearch=${encodeURIComponent(investorName)}`
		: '';

	const overviewDetails = [
		{
			id: 'first_name',
			label: tMipManage('kyc.table.first_name'),
			value: currentInvestor?.user?.firstName,
		},
		{
			id: 'last_name',
			label: tMipManage('kyc.table.last_name'),
			value: currentInvestor?.user?.lastName,
		},
		{
			id: 'email',
			label: tMipManage('kyc.table.email'),
			value: <DetailInfoText>{currentInvestor?.user?.email}</DetailInfoText>,
		},
		{
			id: 'address',
			label: tMipManage('kyc.table.address'),
			value: currentInvestor?.singleAddress || LONG_DASH,
		},
		{
			id: 'date_of_birth',
			label: tMipManage('kyc.table.date_of_birth'),
			value: currentInvestor?.user?.birthDate
				? formatBirthday(currentInvestor?.user?.birthDate)
				: LONG_DASH,
		},
		{
			id: 'phone',
			label: tMipManage('kyc.table.phone'),
			value: (
				<PhoneNumber
					telephonePrefix={currentInvestor?.user?.telephonePrefix}
					phoneNumber={currentInvestor?.user?.phoneNumber}
				/>
			),
		},
		{
			id: 'investmentType',
			label: tMipManage('kyc.table.columns.type'),
			value: (() => {
				const displayType = getInvestmentDisplayType(investmentType ?? null);
				return (
					<StatusBadge
						text={getInvestmentDisplayTypeLabel(displayType)}
						color={getInvestmentDisplayTypeBadgeColor(displayType)}
					/>
				);
			})(),
		},
		...(investmentType
			? [
					{
						id: 'linkedInvestment',
						label: tMipManage('kyc.overview.linked_investment'),
						value: (
							<StyledLink to={investmentsPath}>
								{tMipManage('kyc.overview.view_investment')}
							</StyledLink>
						),
					},
				]
			: []),
	];
	const declarations = useDeclarations(tMipManage, tInvestor, currentInvestor, {
		isBeneficialOwnershipRequired: kycSetting?.isBeneficialOwnershipRequired,
		isSourceOfFundsRequired: kycSetting?.isSourceOfFundsRequired,
		isPepRequired: kycSetting?.isPepRequired,
	});

	return (
		<FlexExtended
			borderRight={`1px solid ${theme.colors.gray[4]}`}
			flexGrow={1}
			maw="15%"
			miw="25rem"
			verticalScroll
		>
			<Flex direction="column" m="1rem">
				<Title order={5} mb="0rem" c={theme.colors.gray[9]} tt="initial" fw={600}>
					{tMipManage('kyc.modal.overview')}
				</Title>
				<Flex mt="1rem" direction="column" gap="0.75rem">
					{overviewDetails.map(({ id, label, value }) => (
						<DetailRow key={id} label={label}>
							{value}
						</DetailRow>
					))}

					{/* show declarations if the currentStep is not on the declarations step */}
					{currentStep?.type !== 'DECLARATIONS' && (
						<>
							<Title
								order={5}
								mb="0rem"
								c={theme.colors.gray[9]}
								tt="initial"
								fw={600}
							>
								{tMipManage('kyc.modal.declaration')}
							</Title>
							{declarations.map((declaration) => (
								<DetailRow key={declaration.id} label={declaration.label}>
									{declaration.value}
								</DetailRow>
							))}
						</>
					)}
				</Flex>
			</Flex>
		</FlexExtended>
	);
};
